/**
 * Narrative Registry
 *
 * Central registry of available narratives by category and location.
 * This registry should be updated as new narratives are generated.
 *
 * Purpose: Filter location selector to show only locations with narratives,
 * preventing "No Data Available" errors when users navigate to locations
 * without narrative content.
 */

/**
 * Registry of location IDs with available narratives, organized by category
 */
const NARRATIVE_REGISTRY = {
  // Health narratives (JSON format in /public/data/narratives/)
  health: new Set([
    'd-kayunga',
    'd-masindi',
    's-kayunga-busaana',
    's-kayunga-galiraya',
    's-kayunga-kangulumira',
    's-masindi-labongo',
    's-masindi-miirya',
    's-masindi-pakanyi',
  ]),

  // Education narratives (HTML format in /public/narratives/html/)
  education: new Set([
    'd-kayunga',
    'd-masindi',
    's-kayunga-busaana',
    's-kayunga-galiraya',
    's-kayunga-kangulumira',
    's-masindi-miirya',
  ]),
}

/**
 * Check if a narrative exists for a specific location and category
 *
 * @param {string} locationId - Location ID (e.g., 'd-kayunga', 's-kayunga-busaana')
 * @param {string} category - Category ('health' or 'education')
 * @returns {boolean} True if narrative exists
 */
export function hasNarrative(locationId, category = 'health') {
  return NARRATIVE_REGISTRY[category]?.has(locationId) || false
}

/**
 * Get all location IDs with available narratives for a category
 *
 * @param {string} category - Category ('health' or 'education')
 * @returns {Set<string>} Set of location IDs with narratives
 */
export function getNarrativeLocations(category = 'health') {
  return NARRATIVE_REGISTRY[category] || new Set()
}

/**
 * Get count of available narratives for a category
 *
 * @param {string} category - Category ('health' or 'education')
 * @returns {number} Number of locations with narratives
 */
export function getNarrativeCount(category = 'health') {
  return NARRATIVE_REGISTRY[category]?.size || 0
}

/**
 * Filter hierarchical locations data to only include locations with narratives
 *
 * This function filters the locations data structure to only show locations
 * that have narrative content available for the specified category.
 *
 * The filtering is hierarchical:
 * - Districts are included if they have a narrative OR any of their subcounties do
 * - Subcounties are included if they have a narrative
 * - This preserves navigation structure while hiding unavailable locations
 *
 * @param {Object} locationsData - Full locations data structure
 * @param {string} category - Category to filter by ('health' or 'education')
 * @returns {Object|null} Filtered locations data or null if no data provided
 */
export function filterLocationsByNarratives(
  locationsData,
  category = 'health'
) {
  if (!locationsData || !locationsData.districts) return null

  const narrativeSet = getNarrativeLocations(category)

  const filteredDistricts = locationsData.districts
    .map(district => {
      // Filter subcounties that have narratives
      const filteredSubcounties = district.subcounties?.filter(subcounty =>
        narrativeSet.has(subcounty.id)
      )

      // Include district if it has a narrative OR has subcounties with narratives
      const hasDistrictNarrative = narrativeSet.has(district.id)
      const hasSubcountyNarratives =
        filteredSubcounties && filteredSubcounties.length > 0

      if (hasDistrictNarrative || hasSubcountyNarratives) {
        return {
          ...district,
          subcounties: filteredSubcounties || [],
        }
      }

      return null
    })
    .filter(Boolean) // Remove null districts

  return {
    ...locationsData,
    districts: filteredDistricts,
  }
}

/**
 * Get a default location ID for a category
 * Returns the first available location (district-level preferred)
 *
 * @param {string} category - Category ('health' or 'education')
 * @returns {string|null} Default location ID or null if no narratives
 */
export function getDefaultLocationForCategory(category = 'health') {
  const locations = Array.from(getNarrativeLocations(category))

  // Prefer district-level locations (start with 'd-')
  const districtLocation = locations.find(id => id.startsWith('d-'))
  if (districtLocation) return districtLocation

  // Fall back to first available location
  return locations[0] || null
}
